import { Box, Text } from '@chakra-ui/react';

import {
  MarketplaceSubEvent,
  OfferActionSurface,
} from '../../../../../../../services/analytics/marketplaceEvents';
import { usePatchLeadByExternalIdOffersOfferId } from '../../../../../../../services/marketplace/generated/kubb/hooks/usePatchLeadByExternalIdOffersOfferId';
import { formatMoney } from '../../../../../../../utils/helpers';
import { useOfferAnalytics } from '../OfferAnalyticsContext';
import { useOfferActionFeedback } from '../useOfferActionFeedback';
import { MarketplaceOffer, OfferCardView, isOfferStale } from '../utils';
import OfferActionModal from './OfferActionModal';
import OfferModalActions from './OfferModalActions';
import StaleOfferBanner from './StaleOfferBanner';

type WithdrawCounterModalProps = {
  card: OfferCardView;
  surface: OfferActionSurface;
  liveOffers: MarketplaceOffer[];
  externalId: string;
  onClose: () => void;
  onSuccess: () => void;
};

const WithdrawCounterModal = ({
  card,
  surface,
  liveOffers,
  externalId,
  onClose,
  onSuccess,
}: WithdrawCounterModalProps) => {
  const { mutate, isPending } = usePatchLeadByExternalIdOffersOfferId();
  const { refetchOffers, notifyOfferChanged } = useOfferActionFeedback(externalId);
  const { trackOfferEvent } = useOfferAnalytics();
  const isStale = isOfferStale(card.driverCounter ?? undefined, liveOffers);

  const eventContext = {
    offerId: card.offer.id,
    dealerLabel: card.dealerLabel,
    dealerAmount: card.amount,
    surface,
  };

  const handleWithdraw = () => {
    mutate(
      {
        externalId,
        offerId: card.driverCounter?.id ?? '',
        data: { action: 'withdraw' },
      },
      {
        onSuccess: () => {
          trackOfferEvent(MarketplaceSubEvent.CounterWithdrawConfirmed, {
            ...eventContext,
            driverAmount: card.driverCounterAmount ?? undefined,
          });
          refetchOffers();
          onSuccess();
          onClose();
        },
        onError: () => {
          trackOfferEvent(MarketplaceSubEvent.OfferActionFailed, {
            ...eventContext,
            actionType: 'withdraw',
          });
          notifyOfferChanged();
          onClose();
        },
      },
    );
  };

  return (
    <OfferActionModal
      isOpen
      onClose={isPending ? () => undefined : onClose}
      title={`Withdraw your counter on ${card.dealerLabel}?`}
    >
      <Box px={{ base: '24px', md: '32px' }} pb={{ base: '24px', md: '32px' }} pt="16px">
        {isStale && <StaleOfferBanner />}

        <Text fontSize="14px" color="gray.600" lineHeight="1.6" mb="24px">
          Your <strong>{formatMoney(card.driverCounterAmount)}</strong> counter will be canceled.{' '}
          The buyer's <strong>{formatMoney(card.amount)}</strong> offer will become available to
          accept again.
        </Text>

        <OfferModalActions
          onCancel={onClose}
          onPrimary={handleWithdraw}
          primaryLabel="Withdraw Counter"
          isPending={isPending}
          isPrimaryDisabled={isStale}
        />
      </Box>
    </OfferActionModal>
  );
};

export default WithdrawCounterModal;
